import { ArrowLeftIcon, WalletIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import {
  PaymentStateBadge,
  usePaymentMethodLabel,
} from "@/features/payments/PaymentStateBadge"
import { isFeatureOff, useCustomerStatement } from "@/features/payments/use-payments"
import { FeatureOff } from "@/components/FeatureOff"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import type { PaymentResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * One customer's money position — §17.5's statement, as a SCREEN.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS IS FOR. The phone rings, the buyer's accounts clerk asks what is
 * outstanding, and the answer has to be on one screen with the bills behind it —
 * not reconstructed by opening seven invoices in turn. §17.5 names exactly this:
 * "show outstanding per customer, generate a statement".
 *
 * A SCREEN, NOT A PDF, AND THAT IS STATED RATHER THAN HALF-BUILT. A statement
 * that prints is a document the customer treats as a demand: it needs its own
 * §9-style layout decisions, its own place in the PDF router and a deliberate
 * answer to what it says about disputed lines. None of that is in this pass, so
 * there is no download button here and no half-working one either.
 *
 * IT LIVES AT `/customers/:id/statement`, BESIDE THE CUSTOMER EDITOR RATHER THAN
 * REPLACING IT. `/customers/:id` is the record — name, GSTIN, address, the
 * things that print on the next bill. This is what they owe, which is a
 * different question with a different capability behind it (`payment.view`,
 * which the ACCOUNTANT holds and `customer.edit` is not).
 *
 * EVERY FIGURE IS THE SERVER'S (§6). `totals` arrives computed; nothing on this
 * screen adds a column up, and `billedPaise` is deliberately the total of the
 * bills LISTED rather than of everything ever billed — the arithmetic the reader
 * can check is the arithmetic that gets trusted.
 *
 * THE RECEIPTS LIST IS EVERY RECEIPT AGAINST THIS BUYER, including those that
 * settled a bill in full and therefore took it off the list above. That is what
 * the owner is looking for with the customer on the phone, and omitting it would
 * make the statement disagree with the bank for reasons nothing on screen
 * explains.
 *
 * WHICH MEANS THIS SCREEN CARRIES TWO SCOPES, AND EVERY FIGURE ON IT SAYS WHICH
 * ONE IT BELONGS TO (2026-08-16). The position card is arithmetic over the bills
 * listed below it — billed − received = still owed, a subtraction the reader
 * performs with his eye — so its "Received" line is captioned "on the bills
 * below", never "Received" alone. The receipts card is the wider set, so it
 * carries its OWN total from the server (`allReceiptsPaise`). Before that second
 * total existed, a customer whose only receipt had settled a bill in full read
 * "Received ₹0.00" an inch above a card headed "Money received" listing ₹20,000
 * — the zero was right about the bills and looked like a lie about the bank.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function CustomerDetailScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const formatDay = useFormatDay()

  const query = useCustomerStatement(id, { enabled: can("payment.view") })

  /*
   * §18.3 — a 404 is "the module is off", not "no such customer". No retry
   * button: pressing it again cannot switch a flag on. And never a permission
   * message, which is precisely the leak the 404 exists to prevent.
   */
  if (query.isError && isFeatureOff(query.error)) {
    return (
      <Shell>
        {/* ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
          * `components/FeatureOff.tsx`. */}
        <FeatureOff module={t("payment.moduleName")} icon={<WalletIcon />} />
      </Shell>
    )
  }

  if (query.isError) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      </Shell>
    )
  }

  if (query.isPending || query.data === undefined) {
    return (
      <Shell>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-48 w-full" />
      </Shell>
    )
  }

  const statement = query.data
  const name = statement.customerName ?? t("lbl.customerUnknown")

  return (
    <Shell>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{name}</h1>
          <p className="text-muted-foreground text-sm">{t("payment.statementSubtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />
          {/* The customer RECORD, which is a different thing from what they owe
            * — editing the GSTIN changes what future bills print and nothing
            * about the ones listed here (§4). */}
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link to={routes.customer(statement.customerId)} />}
          >
            {t("payment.openCustomer")}
          </Button>
        </div>
      </header>

      {/*
        * THE POSITION, FIRST AND IN THREE FIGURES, ALL THREE ABOUT THE BILLS
        * LISTED BELOW. `billedPaise` is not everything ever billed to this
        * buyer, and `receivedPaise` is not everything ever received from him —
        * a settled bill is absent from the list, so counting either would
        * produce a figure whose parts are not on the screen. Both labels say so;
        * the receipts card below carries the wider total under its own heading.
        */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("payment.positionTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="flex w-full max-w-sm flex-col gap-2 text-sm">
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">{t("payment.billedListed")}</dt>
              <dd className="tabular-nums">{formatRupees(statement.totals.billedPaise)}</dd>
            </div>
            <div className="flex justify-between gap-6">
              {/* SCOPED, like `billedListed` beside it. A bare "Received" here
                * would be read against the receipts card below, which lists more
                * — this figure is only about the bills in this subtraction. */}
              <dt className="text-muted-foreground">{t("payment.receivedListed")}</dt>
              <dd className="tabular-nums">{formatRupees(statement.totals.receivedPaise)}</dd>
            </div>
            {/*
              * §17.3 — the third term, so the subtraction still works BY EYE.
              *
              * The server computes `billed − received − credited = outstanding`
              * over exactly the bills listed below. Without this row a customer
              * with one credited bill sees three figures that do not subtract,
              * on the screen whose whole purpose is to be checkable — and §17.5's
              * two-scopes rule exists because a figure the rows beside it cannot
              * explain is a figure nobody believes.
              *
              * SCOPED TO THE BILLS LISTED, exactly like `receivedListed` above
              * it: a note that reduced a bill to nothing took that bill off the
              * list, and counting it here would reintroduce the very mismatch
              * this row is fixing. Hidden at zero, which is every customer until
              * one of their bills is credited.
              */}
            {/* `> 0` rather than `!== 0` — see the same guard on PaymentsCard.
              * The field is required on the wire, and a `> 0` test additionally
              * renders nothing rather than handing `undefined` to `formatPaise`,
              * which throws and takes the whole statement down with it. */}
            {statement.totals.creditedPaise > 0 ? (
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{t("payment.creditedListed")}</dt>
                <dd className="tabular-nums">{formatRupees(statement.totals.creditedPaise)}</dd>
              </div>
            ) : null}
            <Separator />
            <div className="flex justify-between gap-6 text-base font-semibold">
              <dt>{t("payment.stillOwed")}</dt>
              <dd className="tabular-nums">{formatRupees(statement.totals.outstandingPaise)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* The bills behind the figure. Empty is the good case and says so — a
        * customer who owes nothing still gets a titled statement rather than a
        * 404 that would read as a missing customer. */}
      {statement.invoices.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <WalletIcon />
            </EmptyMedia>
            <EmptyTitle>{t("payment.customerSettledTitle")}</EmptyTitle>
            <EmptyDescription>{t("payment.customerSettledHelp", { customer: name })}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Card className="overflow-hidden py-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("lbl.number")}</TableHead>
                  <TableHead>{t("lbl.date")}</TableHead>
                  <TableHead>{t("lbl.status")}</TableHead>
                  <TableHead className="text-right">{t("payment.billTotal")}</TableHead>
                  <TableHead className="text-right">{t("payment.received")}</TableHead>
                  <TableHead className="text-right">{t("payment.stillOwed")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statement.invoices.map((row) => (
                  <TableRow key={row.invoiceId} className="hover:bg-accent/50 relative">
                    <TableCell className="font-medium tabular-nums">
                      <Link
                        to={routes.invoice(row.invoiceId)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {row.invoiceNumber}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {formatDay(row.invoiceDate)}
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {/* Two facts about one row: the document stands, and the
                          * money has not all arrived. Never one badge for both. */}
                        <DocumentStatusBadge status="ISSUED" kind="invoice" />
                        <PaymentStateBadge state={row.state} />
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatRupees(row.grandTotalPaise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatRupees(row.receivedPaise)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                      {formatRupees(row.outstandingPaise)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/*
        * WHAT HAS COME IN — every receipt against this buyer's bills, newest
        * first, including the ones that settled a bill in full and took it off
        * the list above. This is the half the owner reads out on the phone.
        *
        * IT CARRIES ITS OWN TOTAL, AND THAT TOTAL IS THE SERVER'S. The list is
        * wider than the bills above, so the position card's "Received" cannot
        * account for it; without a figure of its own, a reader adding these rows
        * up in his head arrives at a number the screen never states and then has
        * to guess which of the two is wrong. `allReceiptsPaise` is the sum of
        * exactly these rows, computed where every other money figure is (§6) —
        * this screen still adds nothing up.
        */}
      {statement.payments.length === 0 ? null : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("payment.receiptsTitle")}</CardTitle>
            <CardDescription>
              {t("payment.receiptsTotal", {
                amount: formatRupees(statement.totals.allReceiptsPaise),
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y">
              {statement.payments.map((row) => (
                <ReceiptRow key={row.id} payment={row} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </Shell>
  )
}

/** One receipt on the statement, with the bill it was applied to. */
function ReceiptRow({ payment }: { payment: PaymentResponse }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const methodLabel = usePaymentMethodLabel()

  return (
    <li className="flex items-center gap-3 py-2">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium tabular-nums">{formatDay(payment.paidOn)}</span>
          <span className="text-muted-foreground">{methodLabel(payment.method)}</span>
          <Link
            to={routes.invoice(payment.invoiceId)}
            className="hover:text-primary text-muted-foreground underline-offset-4 hover:underline"
          >
            {t("payment.againstBill")}
          </Link>
        </span>
        {payment.reference === null ? null : (
          <span className="text-muted-foreground truncate text-xs tabular-nums">
            {payment.reference}
          </span>
        )}
      </span>
      <span className="shrink-0 font-medium tabular-nums">
        {formatRupees(payment.amountPaise)}
      </span>
    </li>
  )
}

/** The page frame, so every state above keeps the way back. */
function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Link
        to={routes.customers}
        className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
      >
        <ArrowLeftIcon className="size-4" />
        {t("nav.customers")}
      </Link>
      {children}
    </div>
  )
}
